import {
  addHours,
  differenceInHours,
  format,
  roundToNearestHours,
  subHours,
} from "date-fns";
import { defaults, keyBy, round, sortBy, times } from "lodash";
import type { YAxisProps } from "recharts";
import { CHART_COLORS } from "src/ui/colors";

export const chartBarRadius = {
  sm: 4, // Use for thin columns (ex sync health charts)
  md: 6, // Use only for stacked columns (ex cross audience graph)
  lg: 8, // Use for thick columns (ex funnels)
};

/**
 * Returns the single radius value for a graph bar based on the total number of bars in the chart.
 */
export function getChartBarRadius(numBars: number): number {
  if (numBars >= 8) {
    return chartBarRadius.sm;
  } else {
    return chartBarRadius.lg;
  }
}

/**
 * Returns the column radius values for a graph bar based on the total number of bars in the chart.
 * Only the top two border radii are rounded.
 */
export function getColumnRadius(
  numBars: number,
): [number, number, number, number] {
  const barRadius = getChartBarRadius(numBars);
  return [barRadius, barRadius, 0, 0];
}

export const chartStrokeDasharray = "4 6";

// Dash for a dashed data series (a projection, a comparison line); the grid and axes use chartStrokeDasharray.
export const chartSeriesStrokeDasharray = "10 6";

export const chartFontStyles = {
  fontFamily: "var(--chakra-fonts-mono)",
  letterSpacing: "-0.01em",
  fontSize: "var(--chakra-fontSizes-sm)",
  lineHeight: "var(--chakra-lineHeights-4)",
  fill: CHART_COLORS.text,
  color: CHART_COLORS.text,
  fontFeatureSettings:
    "'salt' on, 'ss01' on, 'ss02' on, 'ss03' on, 'ss04' on, 'ss05' on",
  WebkitFontFeatureSettings:
    "'salt' on, 'ss01' on, 'ss02' on, 'ss03' on, 'ss04' on, 'ss05' on",
  MozFontFeatureSettings:
    "'salt' on, 'ss01' on, 'ss02' on, 'ss03' on, 'ss04' on, 'ss05' on",
};

// Shared axis styles for x- and y-axes.
const axisStyles = {
  ...chartFontStyles,
  stroke: CHART_COLORS.text,
  tick: chartFontStyles,
};

/**
 * Get the styles for the x-axis.
 *
 * `tickAlign`: @default "left"
 *    Aligns the x-axis labels so they don't bump into the right-aligned y-axis.
 *    Only use `center` alignment for custom use cases where the y-axis is left-aligned or hidden.
 */
export const getXAxisStyles = ({
  tickLine = false,
  tickAlign = "left",
}: {
  tickLine?: boolean;
  tickAlign?: "left" | "center";
} = {}) => ({
  ...axisStyles,
  dx: tickAlign === "left" ? -20 : 0,
  tickLine: tickLine ? { stroke: CHART_COLORS.gray.divider } : false,
  axisLine: {
    stroke: CHART_COLORS.gray.divider,
    strokeDasharray: chartStrokeDasharray,
  },
});

type YAxisStylesProps = {
  /**
   * Whether to allow decimals in the y-axis.
   * @default true
   */
  allowDecimals?: boolean;
  /**
   * Whether to show the tick line.
   * @default false
   */
  tickLine?: boolean;
  /**
   * The orientation of the y-axis.
   * @default "right"
   *
   * Only use `left` for one-off cases - avoid unless necessary.
   */
  orientation?: "left" | "right";
};

export const getYAxisStyles = ({
  allowDecimals = true,
  tickLine = false,
  orientation = "right",
}: YAxisStylesProps = {}): YAxisProps => ({
  ...axisStyles,
  tickLine: tickLine ? { stroke: CHART_COLORS.gray.divider } : false,
  allowDecimals: allowDecimals,
  orientation,
  axisLine: false,
});

/**
 * Vertical tooltip cursor line often used in line and area charts.
 */
export const getTooltipCursorStyles = () => ({
  strokeWidth: 2,
  stroke: CHART_COLORS.gray.cursor,
});

export function quantityTickFormatter(
  tick: number,
  zeroAsZero: boolean = false,
): string {
  const absTick = Math.abs(tick);
  if (absTick >= 1000000000) {
    return `${(tick / 1000000000).toFixed(0)}B`;
  } else if (absTick >= 1000000) {
    return `${(tick / 1000000).toFixed(0)}M`;
  } else if (absTick >= 1000) {
    return `${round(tick / 1000, 1)}K`; // ex. "2.5K". Recharts will often choose increments of 500 at this scale and dropping the decimal can make it quite off.
  } else if (absTick == 0) {
    if (zeroAsZero) {
      return "0";
    } else {
      return "";
    }
  } else {
    return tick.toString();
  }
}

export function timeTickFormatter(
  tick: string | number,
  granularity: ChartGranularity,
): string {
  try {
    const parsed = new Date(tick);

    switch (granularity) {
      case ChartGranularity.OneHour:
        return format(parsed, "h:mm");
      case ChartGranularity.OneDay:
        return format(parsed, "h:mmaaa");
      case ChartGranularity.OneDayRta:
        return format(parsed, "haaa");
      case ChartGranularity.ThreeDays:
        return format(
          addHours(parsed, ChartGranularityMetadata[granularity].binHours),
          "EEE-dd",
        );
      case ChartGranularity.SevenDaysRta:
        return format(parsed, "MMM d");
      default:
        return format(parsed, "MMM dd");
    }
  } catch {
    return tick.toString();
  }
}

export const roundTimestampToBin = (
  timestamp: Date,
  startTime: Date,
  binHours: number,
) =>
  addHours(
    startTime,
    round(differenceInHours(timestamp, startTime) / binHours) * binHours,
  );

export function padTimeseriesDataWithBuckets<T>(
  timeseriesData: { timestamp: Date; data: T }[],
  padWith: T,
  granularity: ChartGranularity,
  startTime: Date,
) {
  const granularityMetadata = ChartGranularityMetadata[granularity];
  const buckets = Array.from(
    {
      length: (granularityMetadata.days * 24) / granularityMetadata.binHours,
    },
    (_, i) => ({
      timestamp: addHours(startTime, i * granularityMetadata.binHours),
      data: padWith,
    }),
  );
  const bucketsObject = keyBy(buckets, "timestamp");
  const volumeDataObject: typeof bucketsObject = keyBy(
    timeseriesData,
    "timestamp",
  );
  const merged = defaults(volumeDataObject, bucketsObject);
  const paddedData = sortBy(Object.values(merged), (point) => point.timestamp);
  return paddedData;
}

export enum ChartGranularity {
  OneHour = "1h",
  OneDay = "24h",
  OneDayRta = "1d",
  ThreeDays = "3d",
  SevenDays = "7d",
  SevenDaysRta = "7d-rta",
  ThirtyDays = "30d",
  SixtyDays = "60d",
  NinetyDays = "90d",
}

export const ShortChartGranularities = [
  ChartGranularity.OneDay,
  ChartGranularity.ThreeDays,
  ChartGranularity.SevenDays,
];

export const LongChartGranularities = [
  ChartGranularity.SevenDays,
  ChartGranularity.ThirtyDays,
  ChartGranularity.SixtyDays,
  ChartGranularity.NinetyDays,
];

export const RtaChartGranularities = [
  ChartGranularity.OneHour,
  ChartGranularity.OneDayRta,
  ChartGranularity.SevenDaysRta,
];

/**
 * Compute the start/end time range for a given chart granularity.
 * Rounds up to the nearest hour boundary so cache keys are stable within an hour.
 */
export function getChartTimeRange(granularity: ChartGranularity) {
  const metadata = ChartGranularityMetadata[granularity];
  const endTime = roundToNearestHours(new Date(), { roundingMethod: "ceil" });
  const startTime = subHours(endTime, metadata.days * 24);
  return { startTime, endTime, binHours: metadata.binHours };
}

export const ChartGranularityMetadata: Record<
  ChartGranularity,
  {
    label: string;
    days: number;
    binHours: number;
    binMinutes?: number;
    tickInterval: number;
  }
> = {
  [ChartGranularity.OneHour]: {
    label: "1h",
    days: 1 / 24,
    binHours: 1 / 60,
    binMinutes: 1,
    tickInterval: 9,
  },
  [ChartGranularity.OneDay]: {
    label: "24h",
    days: 1,
    binHours: 1,
    tickInterval: 3,
  },
  [ChartGranularity.OneDayRta]: {
    label: "1d",
    days: 1,
    binHours: 0.5,
    binMinutes: 30,
    tickInterval: 5,
  },
  [ChartGranularity.ThreeDays]: {
    label: "3d",
    days: 3,
    binHours: 3,
    tickInterval: 7,
  },
  [ChartGranularity.SevenDays]: {
    label: "7d",
    days: 7,
    binHours: 6,
    tickInterval: 3,
  },
  [ChartGranularity.SevenDaysRta]: {
    label: "7d",
    days: 7,
    binHours: 3,
    tickInterval: 7,
  },
  [ChartGranularity.ThirtyDays]: {
    label: "30d",
    days: 30,
    binHours: 24 * 7,
    tickInterval: 4,
  },
  [ChartGranularity.SixtyDays]: {
    label: "60d",
    days: 60,
    binHours: 24 * 7 * 2,
    tickInterval: 6,
  },
  [ChartGranularity.NinetyDays]: {
    label: "90d",
    days: 90,
    binHours: 24 * 7 * 3,
    tickInterval: 3,
  },
};

export const ticksFromEndTimeForGranularity = (
  granularity: ChartGranularity,
  endTime: Date,
) => {
  const granularityMetadata = ChartGranularityMetadata[granularity];
  const numTicks =
    (granularityMetadata.days * 24) / granularityMetadata.binHours + 1;
  return times(numTicks, (i) =>
    subHours(endTime, i * granularityMetadata.binHours).valueOf(),
  ).toReversed();
};

export const ChartColors = {
  green: {
    base: CHART_COLORS.green.base,
    hover: CHART_COLORS.green.baseHover,
  },
  blue: {
    base: CHART_COLORS.blue.base,
    hover: CHART_COLORS.blue.baseHover,
  },
  red: {
    base: CHART_COLORS.red.base,
    hover: CHART_COLORS.red.baseHover,
  },
  gray: {
    base: CHART_COLORS.gray.base,
    hover: CHART_COLORS.gray.baseHover,
  },
};

export const DEFAULT_GHOST_SURFACE = "base.white";

/** Radial fade in the surface color so a message stays legible over a ghost. */
export const ghostMessageFade = (surfaceColor: string) =>
  `radial(ellipse 55% 60% at 50% 50%, ${surfaceColor} 42%, transparent 100%)`;
